import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, ReplaySubject, Subject, switchMap, take, tap, takeUntil, catchError, of } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { AppConfigService } from 'app/app-config.service';


export interface RequestInbox {
    inboxId: number;
    isRead: boolean;
}
export interface Inbox {
    inboxId: number;
    userId: number;
    referenceID: string;
    subject: string;
    message: string;
    url: string;
    inboxType: number;
    isRead: boolean;
    isDeleted: boolean;
    createDate: string;
    updateDate: string;
}
export interface Notification {
    id: string;
    icon?: string;
    image?: string;
    title?: string;
    description?: string;
    time: string;
    link?: string;
    useRouter?: boolean;
    read: boolean;
}
export interface ReqRead {
    id: string;
    isRead: boolean;
}

export interface ResponseNoti {
    userId: number;
    notiData: Notification;
}


@Injectable({
    providedIn: 'root'
})
export class NotificationsService {

    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private userId: number = 0;
    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient,
        private configService: AppConfigService,
        private _userService: UserService) {


    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    getAllNoti(): Observable<Notification[]> {

        return this._userService.getDataUser().pipe(
            take(1),
            switchMap((res) => {
                if (res && res?.id !== 0) {

                    this.userId = res.id;
                    return this._httpClient.get<Notification[]>(`${this.configService.getConfiguration().apiUrl}/Noti/GetInbox/${res.id}`).pipe(
                        tap((notifications) => {

                            this._notifications.next(notifications);
                        }),
                        catchError((error) => {
                            console.error('Error fetching notifications:', error);
                            return of([]);
                        })
                    );
                } else {

                    return of([]);
                }
            }),
            catchError((error) => {

                console.error('Error fetching user data:', error);
                return of([]);
            })
        );
    }

    setRead(id: string, isRead: boolean): Observable<Notification> {
        const req = {
            id: id,
            isRead: isRead
        } as ReqRead;
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<Notification>(`${this.configService.getConfiguration().apiUrl}/Noti/ReadInbox`, req).pipe(
                map((updatedNotification: Notification) => {

                    // Find the index of the updated notification
                    const index = notifications.findIndex(item => item.id === id);

                    // Update the notification
                    notifications[index] = updatedNotification;

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Return the updated notification
                    return updatedNotification;
                })
            ))
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<boolean>(`${this.configService.getConfiguration().apiUrl}/Noti/UpdateInbox`, id).pipe(
                map((isDeleted: boolean) => {

                    // Find the index of the deleted notification
                    const index = notifications.findIndex(item => item.id === id);

                    // Delete the notification
                    notifications.splice(index, 1);

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Return the deleted status
                    return isDeleted;
                })
            ))
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean> {
        const req = {
            id: this.userId.toString(),
            isRead: true
        } as ReqRead;
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<boolean>(`${this.configService.getConfiguration().apiUrl}/Noti/ReadInboxAll`, req).pipe(
                map((isUpdated: boolean) => {

                    // Go through all notifications and set them as read
                    notifications.forEach((notification, index) => {
                        notifications[index].read = true;
                    });

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Return the updated status
                    return isUpdated;
                })
            ))
        );
    }

}
